import { useIsMutating } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ChristmasImageOptions,
  GetPortraitResponse,
} from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { arrivalStage, lacksChristmasImage, Stage } from "./portraitStages";
import {
  isAnyJobUnderWay,
  isUnderWay,
  useChoosePortrait,
  useCutOutAgain,
  useRequestChristmasImage,
  useUploadPortrait,
} from "./usePortrait";

/** A choice goes onto the cards right away, so it waits here until it is submitted */
export type PendingChoice = "christmasImage" | "photoOnly";

export type Notice = "christmasImageKept" | "photoOnlyKept" | "submitted";

const DEFAULT_OPTIONS: ChristmasImageOptions = {
  outfit: false,
  method: "focused",
};

const withoutPendingChristmasImage = (pending: PendingChoice | undefined) =>
  pending === "christmasImage" ? undefined : pending;

export const usePortraitFlow = (
  portrait: GetPortraitResponse,
  { generateOnArrival }: { generateOnArrival: boolean },
) => {
  const [stage, setStage] = useState(() => arrivalStage(portrait));
  const [pending, setPending] = useState<PendingChoice>();
  const [notice, setNotice] = useState<Notice>();
  const [options, setOptions] = useState(
    portrait.christmasImage?.options ?? DEFAULT_OPTIONS,
  );
  const upload = useUploadPortrait();
  const cutOutAgain = useCutOutAgain();
  const requestChristmasImage = useRequestChristmasImage();
  const choose = useChoosePortrait();
  // The model backend works one job at a time, so everything waits for it
  const busy = useIsMutating() > 0 || isAnyJobUnderWay(portrait);

  const open = (next: Stage) => {
    setNotice(undefined);
    setStage(next);
  };

  const { mutate: mutateChristmasImage } = requestChristmasImage;
  const generate = useCallback(
    (chosenOptions: ChristmasImageOptions) =>
      mutateChristmasImage(chosenOptions, {
        onSuccess: () => setPending(withoutPendingChristmasImage),
        onSettled: () => {
          setNotice(undefined);
          setStage("christmasImage");
        },
      }),
    [mutateChristmasImage],
  );

  // Every new cut-out drops the Christmas image, so a new one follows it
  const followCutout = useRef(generateOnArrival && isUnderWay(portrait.cutout));
  const cutoutOutcome = isUnderWay(portrait.cutout)
    ? undefined
    : portrait.cutout?.status;
  useEffect(() => {
    if (!followCutout.current || cutoutOutcome === undefined) {
      return;
    }
    followCutout.current = false;
    if (cutoutOutcome === "done") {
      generate(options);
    }
  }, [cutoutOutcome, generate, options]);

  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current) {
      return;
    }
    arrived.current = true;
    if (
      generateOnArrival &&
      stage === "christmasImage" &&
      lacksChristmasImage(portrait)
    ) {
      generate(options);
    }
  }, [generateOnArrival, stage, portrait, generate, options]);

  const followNewCutout = () => {
    followCutout.current = true;
    setPending(withoutPendingChristmasImage);
  };

  const savedChoice = portrait.choice?.kind;
  const shownChoice = pending ?? savedChoice;

  return {
    stage,
    busy,
    notice,
    options,
    setOptions,
    shownChoice,
    hasUnsavedChoice: pending !== undefined,
    canGoBackToCard: stage !== "card" && shownChoice !== undefined,
    upload,
    cutOutAgain,
    requestChristmasImage,
    choose,
    open,
    generate,
    takePhoto: (photo: File) =>
      upload.mutate(photo, { onSuccess: followNewCutout }),
    redoCutout: () =>
      cutOutAgain.mutate(undefined, { onSuccess: followNewCutout }),
    openChristmasImage: () => {
      open("christmasImage");
      if (lacksChristmasImage(portrait)) {
        generate(options);
      }
    },
    keepChristmasImage: () => {
      const alreadySaved = portrait.christmasImage?.chosen === true;
      setPending(alreadySaved ? undefined : "christmasImage");
      setStage("card");
      setNotice(alreadySaved ? undefined : "christmasImageKept");
    },
    goWithoutChristmasImage: () => {
      const alreadySaved = savedChoice === "photoOnly";
      setPending(alreadySaved ? undefined : "photoOnly");
      setStage("card");
      setNotice(alreadySaved ? undefined : "photoOnlyKept");
    },
    submit: () => {
      if (pending === undefined) {
        return;
      }
      choose.mutate(
        { withChristmasImage: pending === "christmasImage" },
        {
          onSuccess: () => {
            setPending(undefined);
            setNotice("submitted");
          },
        },
      );
    },
  };
};

export type PortraitFlow = ReturnType<typeof usePortraitFlow>;
